import { DEMAND } from "@demandproai/shared";
import DemandStudioPreviewClientSelect from "./DemandStudioPreviewClientSelect";
import PdfPreviewSkeleton from "./PdfPreviewSkeleton";
import XSelect from "../common/XSelect";
import usePreviewIframeReady from "../../hooks/usePreviewIframeReady";

const innerClasses = "h-full flex flex-col gap-2";
const selectWrapClasses = "w-80";
const selectLabelClasses = "block mb-1 text-xs font-semibold text-content";
const selectClasses = "text-sm";
const errorClasses = "text-danger mb-2";

const stageClasses = "relative flex-1 min-h-0";
const layerBaseClasses =
  "absolute inset-0 transition-opacity duration-200 ease-out";
const skeletonLayerVisibleClasses = `${layerBaseClasses} opacity-100 z-10 pointer-events-none`;
const skeletonLayerHiddenClasses = `${layerBaseClasses} opacity-0 z-10 pointer-events-none`;
const iframeBaseClasses =
  "w-full h-full border border-border rounded-lg block";
const iframeLayerClasses = `${iframeBaseClasses} ${layerBaseClasses}`;

const notAvailableClasses =
  "absolute inset-0 flex items-center justify-center text-body";

const DemandStudioPreview = ({ previewState, hideSelectors = false, forceSkeleton = false }) => {
  const {
    viewerUrl,
    isLoading,
    error,
    demandOptions = [],
    selectedDemandType,
    onSelectDemandType,
    lifecareClientOptions,
    selectedLifecareClientId,
    onSelectLifecareClient,
    docxOptions,
    selectedDocxUrl = "",
    onSelectDocx,
  } = previewState;
  const isLifecareSelected = selectedDemandType === DEMAND.Lifecare_Plan;

  const { isReady: isIframeReady, handleLoad: handleIframeLoad } =
    usePreviewIframeReady({ src: viewerUrl });

  const isPreparingPreview =
    forceSkeleton || isLoading || (Boolean(viewerUrl) && !isIframeReady);
  const shouldShowNotAvailable = !isLoading && !viewerUrl && !error && !forceSkeleton;
  const isIframeVisible = isIframeReady && !forceSkeleton;

  return (
    <div className={innerClasses}>
      {!hideSelectors && demandOptions.length > 1 && (
        <div className={selectWrapClasses}>
          <label htmlFor="demand-studio-demand-type-select" className={selectLabelClasses}>
            Select demand type
          </label>
          <XSelect
            id="demand-studio-demand-type-select"
            className={selectClasses}
            value={selectedDemandType}
            onChange={(event) => onSelectDemandType({ value: event.target.value })}
            options={demandOptions}
            emptyOption={false}
          />
        </div>
      )}
      {!hideSelectors && isLifecareSelected && (
        <DemandStudioPreviewClientSelect
          options={lifecareClientOptions}
          selectedClientId={selectedLifecareClientId}
          onSelectClient={onSelectLifecareClient}
        />
      )}
      {!hideSelectors && docxOptions.length > 1 && (
        <div className={selectWrapClasses}>
          <label htmlFor="demand-studio-docx-select" className={selectLabelClasses}>
            Select document
          </label>
          <XSelect
            id="demand-studio-docx-select"
            className={selectClasses}
            value={selectedDocxUrl}
            onChange={(event) => onSelectDocx({ value: event.target.value })}
            options={docxOptions}
            emptyOption={false}
          />
        </div>
      )}
      {error && <div className={errorClasses}>{error}</div>}
      <div className={stageClasses}>
        <div
          className={isPreparingPreview ? skeletonLayerVisibleClasses : skeletonLayerHiddenClasses}
          aria-hidden={!isPreparingPreview}
        >
          <PdfPreviewSkeleton />
        </div>
        {shouldShowNotAvailable && (
          <div className={notAvailableClasses}>Document preview not available.</div>
        )}
        {viewerUrl && (
          <iframe
            key={viewerUrl}
            title="Demand Preview"
            src={viewerUrl}
            onLoad={handleIframeLoad}
            style={{ border: 0 }}
            allowFullScreen
            className={`${iframeLayerClasses} ${isIframeVisible ? "opacity-100" : "opacity-0"}`}
          />
        )}
      </div>
    </div>
  );
};

export default DemandStudioPreview;
